import React, { ReactNode, forwardRef, useMemo } from 'react';
import { useId, usePress } from 'react-aria';
import { getPlatformStyles, isMobile } from '@direct-frontend/platform-code';
import { genericMemo, type DistributiveOmit } from '@direct-frontend/stdlib';

import { HStack } from '../Stack';
import { cn } from '../../lib/css/className/className';
import { TextInput } from '../TextInput';
import { useObjectRef } from '../../hooks/useObjectRef';
import { HelpProps } from '../Help';
import { TextInputProps } from '../TextInput/types';
import { TextGenericWithSkin } from '../TextGeneric/TextGenericWithSkin';

getPlatformStyles({
    desktop: import('./TextInputCompact@desktop.css'),
});

const cls = cn('TextInputCompact');

type TextInputCompactForbiddenProps = 'color' | 'textAlign' | 'hasCounter' | 'hasClearButton' | 'customLabelChild';

// поскольку каждый из TextInputProps зависит от TextInputBaseProps, то нужно переопределить типы по-отдельности
export type TextInputCompactProps = {
    /**
     * Подпись к полю
     */
    label?: string;

    /**
     * Компонент со всплывающей подсказкой при клике на знак вопроса.
     * При передаче данного параметра рядом с label рисуется иконка `Help` соответствующего размера.
     * @param helpProps - параметры для передадчи во всплывающую подсказку (управляют размером, цветом и выравниванием)
     */
    help?: (helpProps: HelpProps) => ReactNode;
} & DistributiveOmit<TextInputProps, TextInputCompactForbiddenProps> & {
        /**
         * Размер контрола (проксируется в TextInput и используется для размера лейбла)
         * По умолчанию: 'm'
         */
        size?: TextInputProps['size'];
    };

/**
 * Компонент для отображения компактного поля ввода
 */
export const TextInputCompact = genericMemo(
    forwardRef<HTMLTextAreaElement | HTMLInputElement, TextInputCompactProps>(
        function TextInputCompact(props, forwardedRef) {
            const { className, addonLeft, ...textInputProps } = props;
            const label = props.label;
            const help = props.help;

            const size = props.size ?? 'm';

            // генерация id для aria-свойств компонента
            const inputFieldRef = useObjectRef(forwardedRef);
            const inputFieldId = useId(props.id);
            const labelId = useId(props.labelId);

            // usePress нужен, чтобы отменить клик и чтобы в модалках не рисовался лишний outline
            const { pressProps } = usePress({
                isDisabled: props.isDisabled || props.isLoading,
                allowTextSelectionOnPress: true,
            });

            // подготовка компонента для отображения подписи
            const labelComponent = useMemo(() => {
                return (
                    <label
                        {...pressProps}
                        id={labelId}
                        aria-label={label}
                        htmlFor={inputFieldId}
                        className={cls('label-wrapper')}
                    >
                        <TextGenericWithSkin
                            skinSize={size}
                            textSize="current-body"
                            color="current"
                            role="supplementary"
                            className={cls('label')}
                            help={help}
                        >
                            {label}
                        </TextGenericWithSkin>
                    </label>
                );
            }, [pressProps, labelId, label, inputFieldId, help, size]);

            const addonLeftComponent = useMemo(
                () =>
                    addonLeft ? (
                        <HStack alignItems="center" gap="current-geometry" className={cls('addon-left-wrapper')}>
                            {addonLeft}
                            {labelComponent}
                        </HStack>
                    ) : (
                        labelComponent
                    ),
                [addonLeft, labelComponent],
            );

            if (isMobile()) {
                return (
                    <TextInput
                        className={cls({}, [className])}
                        size={size}
                        color="contour"
                        textAlign={'left'}
                        ref={inputFieldRef}
                        {...props}
                    />
                );
            }

            // добавление корректных aria-свойств (нужно поскольку используется label за пределами FormField)
            const ariaProps = {
                'aria-labelledby': label ? labelId : undefined,
                'aria-describedby': [props['aria-describedby'], labelId].filter(Boolean).join(' ') || undefined,
            };

            return (
                <TextInput
                    id={inputFieldId}
                    size={size}
                    color="contour"
                    ref={inputFieldRef}
                    className={cls({}, [className])}
                    addonLeft={addonLeftComponent}
                    {...textInputProps}
                    {...ariaProps}
                />
            );
        },
    ),
);
